import { useState, useEffect } from 'react';
import { TrendingUp, Coins, ShoppingCart, Users } from 'lucide-react';
import { API_URL } from '../../config/api';

const TrendAnalysis = ({ getDateRange, reportStoreFilter, formatCurrency }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState('daily');

  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const dr = getDateRange();
        let params = `start_date=${dr.start}&end_date=${dr.end}&granularity=${granularity}`;
        if (reportStoreFilter === 'gold') params += '&store_id=1';
        else if (reportStoreFilter === 'silver') params += '&store_ids=2,3';
        else if (reportStoreFilter !== 'all') params += `&store_id=${reportStoreFilter}`;

        const res = await fetch(`${API_URL}/reports/trends?${params}`);
        if (res.ok) setData(await res.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetch_();
  }, [getDateRange, reportStoreFilter, granularity]);

  if (loading) return <div className="empty-state"><div className="empty-icon">⏳</div><p>Trend verileri hesaplanıyor...</p></div>;

  const totalRevenue = data.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0);
  const totalSales = data.reduce((s, d) => s + (d.sale_count || 0), 0);
  const avgDailyRevenue = data.length > 0 ? totalRevenue / data.length : 0;
  const maxRevenue = Math.max(...data.map(d => parseFloat(d.total_revenue) || 0), 1);

  // 7 periyotluk hareketli ortalama
  const movingAvg = data.map((d, i) => {
    if (i < 6) return null;
    const slice = data.slice(i - 6, i + 1);
    return slice.reduce((s, x) => s + parseFloat(x.total_revenue || 0), 0) / 7;
  });

  // Basit linear tahmin (son 7 periyottan)
  const forecast = [];
  if (data.length >= 7) {
    const recent = data.slice(-7);
    const avgGrowth = recent.reduce((s, d, i) => {
      if (i === 0) return 0;
      return s + (parseFloat(d.total_revenue) - parseFloat(recent[i - 1].total_revenue));
    }, 0) / 6;
    const lastVal = parseFloat(recent[recent.length - 1].total_revenue) || 0;
    for (let j = 1; j <= 7; j++) {
      forecast.push(Math.max(0, lastVal + avgGrowth * j));
    }
  }

  // Trend yönü
  const recentHalf = data.slice(-Math.ceil(data.length / 2));
  const firstHalf = data.slice(0, Math.ceil(data.length / 2));
  const recentAvg = recentHalf.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0) / (recentHalf.length || 1);
  const firstAvg = firstHalf.reduce((s, d) => s + parseFloat(d.total_revenue || 0), 0) / (firstHalf.length || 1);
  const trendPct = firstAvg > 0 ? ((recentAvg - firstAvg) / firstAvg * 100) : 0;

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[
          { id: 'daily', label: 'Günlük' },
          { id: 'weekly', label: 'Haftalık' },
          { id: 'monthly', label: 'Aylık' },
        ].map(g => (
          <button key={g.id} className={`period-btn ${granularity === g.id ? 'active' : ''}`} onClick={() => setGranularity(g.id)}>{g.label}</button>
        ))}
      </div>

      <div className="stats-grid">
        <div className="stat-card primary">
          <div className="stat-icon"><Coins size={20} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(totalRevenue)}</div>
            <div className="stat-label">Toplam Gelir (Tamirat Dahil)</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><ShoppingCart size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{totalSales}</div>
            <div className="stat-label">Toplam Satış</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><TrendingUp size={20} style={{ color: '#0f766e' }} /></div>
          <div className="stat-info">
            <div className="stat-value">{formatCurrency(avgDailyRevenue)}</div>
            <div className="stat-label">Ort. Dönem Geliri</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon"><TrendingUp size={20} style={{ color: trendPct >= 0 ? '#2ecc71' : '#e74c3c' }} /></div>
          <div className="stat-info">
            <div className="stat-value" style={{ color: trendPct >= 0 ? '#2ecc71' : '#e74c3c' }}>
              {trendPct >= 0 ? '↑' : '↓'} %{Math.abs(trendPct).toFixed(1)}
            </div>
            <div className="stat-label">Trend Yönü</div>
          </div>
        </div>
      </div>

      {/* Gelir Trend Grafiği */}
      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0 }}>Gelir Trendi (Tamirat Dahil)</h3>
          <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
            <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#0f766e', borderRadius: 2, marginRight: 4 }} />Gelir</span>
            <span><span style={{ display: 'inline-block', width: 12, height: 3, background: '#f59e0b', borderRadius: 1, marginRight: 4, verticalAlign: 'middle' }} />7-Ort.</span>
            {forecast.length > 0 && <span><span style={{ display: 'inline-block', width: 12, height: 12, background: 'rgba(59,130,246,0.3)', borderRadius: 2, marginRight: 4 }} />Tahmin</span>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 200, overflowX: 'auto', paddingBottom: 24, position: 'relative' }}>
          {data.map((d, i) => {
            const h = maxRevenue > 0 ? (parseFloat(d.total_revenue) / maxRevenue) * 180 : 0;
            const maH = movingAvg[i] != null ? (movingAvg[i] / maxRevenue) * 180 : null;
            return (
              <div key={d.period} style={{ flex: '1 0 auto', minWidth: data.length > 60 ? 6 : data.length > 30 ? 10 : 16, maxWidth: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                <div
                  title={`${d.period}: ${formatCurrency(d.total_revenue)} (${d.sale_count} satış)`}
                  style={{ width: '80%', height: h, background: '#0f766e', borderRadius: '3px 3px 0 0', opacity: 0.8, transition: 'height 0.2s', position: 'relative' }}
                />
                {maH != null && (
                  <div style={{
                    position: 'absolute', bottom: maH + 24, left: '50%', width: 6, height: 6,
                    background: '#f59e0b', borderRadius: '50%', transform: 'translateX(-50%)',
                    zIndex: 2
                  }} />
                )}
                {data.length <= 31 && (
                  <div style={{ fontSize: 9, color: '#5a6c7d', marginTop: 4, transform: 'rotate(-45deg)', whiteSpace: 'nowrap' }}>
                    {d.period.length > 7 ? d.period.slice(5) : d.period}
                  </div>
                )}
              </div>
            );
          })}
          {/* Forecast bars */}
          {forecast.map((val, i) => {
            const h = maxRevenue > 0 ? (val / maxRevenue) * 180 : 0;
            return (
              <div key={`fc-${i}`} style={{ flex: '1 0 auto', minWidth: data.length > 60 ? 6 : data.length > 30 ? 10 : 16, maxWidth: 40, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '80%', height: h, background: 'rgba(59,130,246,0.3)', borderRadius: '3px 3px 0 0', border: '1px dashed #3b82f6', transition: 'height 0.2s' }} />
                {data.length <= 31 && (
                  <div style={{ fontSize: 9, color: '#93c5fd', marginTop: 4 }}>+{i + 1}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Detay Tablosu */}
      <div className="data-card">
        <h3>Dönem Detayları</h3>
        <div className="table-wrapper" style={{ maxHeight: 400 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Dönem</th>
                <th>Satış Adedi</th>
                <th>Gelir (Tamirat Dahil)</th>
                <th>Ort. Sepet</th>
                <th>Tekil Müşteri</th>
                <th>Değişim</th>
              </tr>
            </thead>
            <tbody>
              {[...data].reverse().slice(0, 60).map((d, i, arr) => {
                const prev = arr[i + 1];
                const change = prev ? ((parseFloat(d.total_revenue) - parseFloat(prev.total_revenue)) / (parseFloat(prev.total_revenue) || 1) * 100) : 0;
                return (
                  <tr key={d.period}>
                    <td><strong>{d.period}</strong></td>
                    <td>{d.sale_count}</td>
                    <td><strong>{formatCurrency(d.total_revenue)}</strong></td>
                    <td>{formatCurrency(d.avg_order)}</td>
                    <td>{d.unique_customers}</td>
                    <td style={{ color: change >= 0 ? '#2ecc71' : '#e74c3c', fontWeight: 600 }}>
                      {prev ? `${change >= 0 ? '↑' : '↓'} %${Math.abs(change).toFixed(1)}` : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TrendAnalysis;
